import React from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";

import PageWrapper from "../components/PageWrapper";
import PeopleOps from "../sections/landing4/PeopleOps";
import TeamCTA from "../sections/landing4/TeamCTA";
import Seo from "../components/Seo";
import OpeningsList from "../sections/openings/OpeningsList";

const HeroSection = styled.section`
  padding: 96px 0 48px;
  background: var(--nd-black);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(
      circle,
      var(--nd-border) 0.5px,
      transparent 0.5px
    );
    background-size: 12px 12px;
    opacity: 0.3;
    pointer-events: none;
  }
`;

const HeroTitle = styled.h1`
  font-family: "Doto", "Space Mono", monospace;
  font-size: 64px;
  font-weight: 700;
  color: var(--nd-text-display);
  letter-spacing: -0.03em;
  line-height: 1;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 40px;
  }
`;

const HeroSubtitle = styled.p`
  font-family: "Space Grotesk", sans-serif;
  font-size: 18px;
  font-weight: 300;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  max-width: 500px;
  margin: 0;
`;

const FounderSection = styled.section`
  padding: 64px 0;
  background: var(--nd-black);
`;

const FounderCard = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FounderLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
`;

const FounderName = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 28px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.01em;
  margin: 0;
`;

const FounderRole = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-secondary);
  margin: 0;
`;

const TeamSection = styled.section`
  padding: 32px 0 64px;
  background: var(--nd-black);
`;

const SectionLabel = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin: 0 0 16px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
`;

const MemberCard = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MemberName = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 20px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.01em;
  margin: 0;
`;

const MemberTitle = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin: 0;
`;

const MemberBio = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  margin: 0;
  flex-grow: 1;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const PrimaryBtn = styled.a`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 12px 24px;
  border-radius: 999px;
  background: var(--nd-text-display);
  color: var(--nd-black) !important;
  border: none;
  cursor: pointer;
  text-decoration: none !important;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: opacity 200ms ease;
  &:hover { opacity: 0.85; color: var(--nd-black) !important; }
`;

const SecondaryBtn = styled.a`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 12px 24px;
  border-radius: 999px;
  background: transparent;
  color: var(--nd-text-primary) !important;
  border: 1px solid var(--nd-border-visible);
  cursor: pointer;
  text-decoration: none !important;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: border-color 200ms ease, color 200ms ease;
  &:hover {
    border-color: var(--nd-text-display);
    color: var(--nd-text-display) !important;
  }
`;

const TeamPage = () => (
  <>
    <Seo
      title="Team"
      description="Fabio Palumbo (Founder, DevOps Architect), Matías (Senior DevOps), Jeremías (DevOps), Nico (Academy graduate). We are also hiring — see open positions."
      pathname="/team"
    />
    <PageWrapper>
    <HeroSection>
      <Container>
        <HeroTitle>Our Team</HeroTitle>
        <HeroSubtitle>The people behind the infrastructure.</HeroSubtitle>
      </Container>
    </HeroSection>

    <FounderSection>
      <Container>
        <FounderCard>
          <FounderLabel>FOUNDER</FounderLabel>
          <FounderName>Fabio Palumbo</FounderName>
          <FounderRole>DevOps Architect · 15 years building cloud infrastructure for startups.</FounderRole>
          <ButtonRow>
            <PrimaryBtn href="/Fabio_Palumbo_CV.pdf" download="Fabio_Palumbo_CV.pdf">
              ↓ Download Resume
            </PrimaryBtn>
            <SecondaryBtn href="https://github.com/fabiopalumbo" target="_blank" rel="noopener noreferrer">
              GitHub ↗
            </SecondaryBtn>
          </ButtonRow>
        </FounderCard>
      </Container>
    </FounderSection>

    <TeamSection>
      <Container>
        <SectionLabel>SENIOR CLOUD ENGINEERS</SectionLabel>
        <CardGrid>
          <MemberCard>
            <MemberTitle>Senior Cloud Engineer · SRE & DevOps</MemberTitle>
            <MemberName>Matías Daniel Quintana</MemberName>
            <MemberBio>
              6+ years scaling AWS workloads. ECS, S3, ElastiCache. Queue-based
              autoscaling, observability stacks (Grafana, Prometheus, Loki), and
              cost optimization. AWS Solution Architect + SysOps + SRE certified.
            </MemberBio>
            <ButtonRow>
              <SecondaryBtn href="https://www.linkedin.com/in/mdquin89" target="_blank" rel="noopener noreferrer">
                LinkedIn ↗
              </SecondaryBtn>
            </ButtonRow>
          </MemberCard>

          <MemberCard>
            <MemberTitle>DevOps Engineer · AWS</MemberTitle>
            <MemberName>Jeremías Manuel López</MemberName>
            <MemberBio>
              CI/CD, Kubernetes, Docker, Terraform, Ansible. Built and operated
              cloud-native pipelines at Globant, Endava, and BairesDev. Strong
              on observability and incident response for production systems.
            </MemberBio>
            <ButtonRow>
              <SecondaryBtn href="https://www.linkedin.com/in/jeremias-manuel-lopez" target="_blank" rel="noopener noreferrer">
                LinkedIn ↗
              </SecondaryBtn>
            </ButtonRow>
          </MemberCard>
        </CardGrid>
      </Container>
    </TeamSection>

    <TeamSection>
      <Container>
        <SectionLabel>TALENT & SALES</SectionLabel>
        <CardGrid>
          <MemberCard>
            <MemberTitle>IT Recruiter · Sales Manager</MemberTitle>
            <MemberName>Julio Nicolas Fiorentino</MemberName>
            <MemberBio>
              18+ years matching senior tech talent across LATAM and global
              markets. Bilingual EN/ES (Portuguese pro). Owns end-to-end
              recruitment and client account growth for Dioscuro engagements.
            </MemberBio>
            <ButtonRow>
              <SecondaryBtn href="https://www.linkedin.com/in/nicofioren" target="_blank" rel="noopener noreferrer">
                LinkedIn ↗
              </SecondaryBtn>
            </ButtonRow>
          </MemberCard>
        </CardGrid>
      </Container>
    </TeamSection>

    <OpeningsList />

    <PeopleOps />
    <TeamCTA />
    </PageWrapper>
  </>
);

export default TeamPage;
